import React from 'react';
import { useTranslation } from 'react-i18next';
import { rowActivation } from './rowActivation';
import { AttributePill, ActionChip } from './pills';

/**
 * TaskCard — the task_board's one card shape, shared by the board tiers (desktop/tablet) and the
 * mobile stage-list. UXeng's spec (§4): the comp's own "No date"/"No parent" strings are CORRECT —
 * they show absence rather than fabricating a default, the same rule as an em-dash for null recency.
 * Rendered MUTED so an absent value never carries the same visual weight as a real one.
 *
 * Tasks-complete-loop (UXeng, 2026-08-23): tapping the card opens the detail panel; Complete is a
 * one-tap ActionChip on the card ITSELF (a task you must open to finish is a task you finish at a
 * desk) — reversible, so the chip toggles Complete/Undo rather than disappearing or asking to confirm.
 */
export interface TaskRow {
  id: string;
  title: string;
  dueDate: string | null;
  completedAt: string | null;
  ownerId: string | null;
  ownerName: string | null;
  parentId: string | null;
  parentTitle: string | null;
}

export const TaskCard: React.FC<{
  task: TaskRow;
  stageLabel: string;
  onEditStage: (task: TaskRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => void;
  onOpenDetail: (taskId: string) => void;
  onToggleComplete: (task: TaskRow) => void;
  sheetShape: 'sheet' | 'popover';
}> = ({ task, stageLabel, onEditStage, onOpenDetail, onToggleComplete, sheetShape }) => {
  const { t } = useTranslation('common');
  const isDone = !!task.completedAt;
  return (
    <div
      className={`p-3 rounded-xl bg-surface border border-border-neutral cursor-pointer ${isDone ? 'opacity-60' : ''}`}
      {...rowActivation(() => onOpenDetail(task.id))}
      data-testid="task-card"
      onClick={() => onOpenDetail(task.id)}
    >
      <div className={`text-sm font-semibold text-text-strong truncate ${isDone ? 'line-through' : ''}`}>{task.title || '—'}</div>
      <div className="text-xs text-text-muted mt-1">
        {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : <span className="text-text-muted/70">{t('taskBoard.noDate')}</span>}
      </div>
      <div className="text-xs text-text-muted">
        {task.parentTitle ? task.parentTitle : <span className="text-text-muted/70">{t('taskBoard.noParent')}</span>}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <AttributePill value={stageLabel} label={t('taskBoard.stage')} emptyText={t('customers.detail.setField', { field: t('taskBoard.stage') })}
          onOpen={(a) => onEditStage(task, a, sheetShape)} />
        <ActionChip label={isDone ? t('taskBoard.undo') : t('taskBoard.complete')} testId="task-complete-chip"
          onAct={() => onToggleComplete(task)} />
        {task.ownerName && <span className="text-[11px] text-text-muted truncate">{task.ownerName}</span>}
      </div>
    </div>
  );
};
